/**
 * A lógica de uma escaramuça, do ponto de vista de quem joga.
 *
 * O motor (@oz/rules) diz o que é legal e o que acontece; este arquivo cuida do
 * caminho até lá — qual unidade está selecionada, para onde o cursor aponta,
 * que menu está aberto. Nenhuma regra é decidida aqui: toda ação passa por
 * applyAction, e o que ele recusar não acontece.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  applyAction, attackableTiles, buildOccupancy, canTalkTo, chestAt, chestOpener, chooseAiAction,
  combatantOf,
  curableBy, endPhase, footprint, forecast, hasEffect, hostileReach, isHostile, isWeaponSlot,
  itemHelps, itemUseOf,
  livingUnits, MAX_INVENTORY, nextAiActor, pathTo, phaseIsOver, reachableTiles,
  covers, samePosition, settle, spanDistance, standableTiles, unitAt, unitById,
  weaponOf,
  type Action, type AiDifficulty, type Content, type Event, type Forecast, type ItemInstance,
  type MatchState, type Team, type Unit, type Vec2,
} from '@oz/rules';

export type Selection =
  | { readonly mode: 'livre' }
  /**
   * O menu de campo: o que se abre confirmando numa casa vazia.
   *
   * É onde mora "encerrar fase". Antes a única forma de passar o turno pelo
   * mouse era um botão fora do tabuleiro, na barra da página — e num jogo
   * tático toda ordem deveria sair de dentro da tela de jogo, como no Fire
   * Emblem de GBA.
   */
  | { readonly mode: 'campo'; readonly index: number }
  | { readonly mode: 'movendo'; readonly unitId: number }
  | { readonly mode: 'menu'; readonly unitId: number; readonly destination: Vec2; readonly path: readonly Vec2[] }
  | {
      readonly mode: 'itens';
      readonly unitId: number;
      readonly destination: Vec2;
      readonly path: readonly Vec2[];
      readonly index: number;
    }
  /**
   * A tela de troca: duas bolsas lado a lado, arrumadas antes de valer.
   *
   * O rascunho fica AQUI, e não no estado da partida, porque enquanto o jogador
   * arrasta itens de um lado para o outro nada aconteceu ainda — sair pelo
   * cancelar tem de devolver as duas bolsas como estavam. Só ao concluir é que
   * o motor recebe as duas listas prontas e decide se a troca vale.
   */
  | {
      readonly mode: 'trocando';
      readonly unitId: number;
      readonly destination: Vec2;
      readonly path: readonly Vec2[];
      readonly targetId: number;
      /** De qual bolsa o cursor está escolhendo. */
      readonly side: 'propria' | 'alheia';
      readonly index: number;
      readonly mine: readonly ItemInstance[];
      readonly theirs: readonly ItemInstance[];
    }
  | {
      readonly mode: 'mirando';
      readonly unitId: number;
      readonly destination: Vec2;
      readonly path: readonly Vec2[];
      /**
       * `item` é escolher em quem usar o item da bolsa — a própria unidade ou
       * o aliado encostado. É a única saída para quem está paralisado: ele não
       * age, e o antídoto tem de vir de outra mão.
       *
       * `conversar` é a conversa que traz o inimigo para o seu lado — ver
       * `Action.conversar` no motor.
       *
       * `trocar` é escolher COM QUEM trocar, antes de a tela de troca abrir.
       * Ela só aparece quando há mais de um companheiro encostado: com um só,
       * perguntar seria uma pergunta de resposta única. Antes dela, a troca
       * pegava sempre o primeiro da lista, e o segundo aliado ao lado era
       * inalcançável — a bolsa dele não abria por meio nenhum.
       */
      readonly kind: 'atacar' | 'curar' | 'item' | 'conversar' | 'trocar';
      readonly targets: readonly number[];
      readonly index: number;
      /** Qual espaço da bolsa, quando a mira é de item. */
      readonly itemIndex?: number | undefined;
    };

export type MenuOption =
  | 'atacar' | 'conversar' | 'curar' | 'bau' | 'itens' | 'trocar' | 'esperar' | 'auto' | 'voltar';

/**
 * As ordens do menu de campo.
 *
 * Tudo o que se pode fazer numa partida sai daqui — inclusive câmera, som e
 * tela cheia, que antes eram botões de página fora do tabuleiro. Fora do
 * tabuleiro eles não existiam para quem joga no controle, e sumiam de vez em
 * tela cheia: o elemento que vai a tela cheia é o campo de batalha, e a barra
 * da página fica atrás dele. Foi assim que ligar a batalha automática em tela
 * cheia virou uma armadilha sem saída.
 */
export type FieldOption =
  | 'encerrar_fase'
  | 'ameaca'
  | 'auto_fase'
  | 'camera'
  | 'som'
  | 'tela_cheia'
  | 'opcoes'
  | 'voltar';

const FIELD_OPTIONS: readonly FieldOption[] = [
  'encerrar_fase', 'ameaca', 'auto_fase', 'camera', 'som', 'tela_cheia', 'opcoes', 'voltar',
];

/** As ordens do menu de campo que mudam de valor com esquerda/direita. */
const FIELD_VALUE_OPTIONS: ReadonlySet<FieldOption> = new Set<FieldOption>(['camera']);

/**
 * Quanto o jogo espera antes de virar a fase sozinho.
 *
 * Curto o bastante para não parecer travado, longo o bastante para a última
 * unidade acabar de perder a cor antes de a faixa do inimigo entrar.
 */
const AUTO_END_PHASE_MS = 850;

export interface SkirmishView {
  readonly state: MatchState;
  readonly selection: Selection;
  readonly cursor: Vec2;
  readonly moveTiles: readonly Vec2[];
  readonly attackTiles: readonly Vec2[];
  /**
   * A mancha de alcance dos inimigos que o jogador mandou mostrar.
   *
   * É o "alcance inimigo" do Fire Emblem: onde cada hostil marcado chega a pé
   * e o que ele atinge de lá, tudo somado numa figura só. Sai vazia na fase da
   * máquina — a leitura serve para decidir ONDE PARAR, e isso só se decide na
   * própria vez.
   */
  readonly threatTiles: readonly Vec2[];
  /** As casas dos próprios inimigos marcados, para marcá-los no tabuleiro. */
  readonly threatMarks: readonly Vec2[];
  /** Quem o jogador marcou a dedo. Os mortos saem sozinhos da lista. */
  readonly threatIds: ReadonlySet<number>;
  /** O interruptor do "todos de uma vez". */
  readonly threatAll: boolean;
  readonly path: readonly Vec2[];
  readonly forecast: Forecast | null;
  readonly hovered: Unit | null;
  readonly target: Unit | null;
  readonly log: readonly string[];
  readonly canAttack: boolean;
  readonly canHeal: boolean;
  /** Com quem a unidade selecionada pode trocar itens, daqui de onde parou. */
  readonly tradePartners: readonly number[];
  /** E com quem ela pode CONVERSAR — o inimigo que muda de lado. */
  readonly talkTargets: readonly number[];
  readonly menuOptions: readonly MenuOption[];
  readonly menuIndex: number;
  readonly fieldOptions: readonly FieldOption[];
  readonly isCpuTurn: boolean;
  readonly isCpuActing: boolean;
  /**
   * O esquadrão do jogador está jogando sozinho.
   *
   * É a mesma inteligência que move o inimigo, apontada para o seu lado. Serve
   * para varrer um mapa já decidido sem clicar unidade por unidade, e para ver
   * o que a IA faria no seu lugar. Continua ligado de um turno para o outro,
   * até o jogador desligar.
   */
  readonly autoBattle: boolean;
  readonly playMode: 'vs_cpu' | 'hotseat';
  readonly difficulty: AiDifficulty;
  readonly cpuSpeed: 'normal' | 'rapido';
}

/**
 * Em quem este item da bolsa pode ser usado, a partir de onde a unidade parou.
 *
 * A própria unidade e os aliados encostados — o mesmo alcance que o motor
 * aceita (`itemUseOf`) —, e só quem ganha alguma coisa com isso: oferecer o
 * antídoto a quem não está envenenado é oferecer uma ordem que o motor recusa.
 */
export function itemTargets(
  state: MatchState,
  content: Content,
  unit: Unit,
  at: Vec2,
  index: number,
): number[] {
  const use = itemUseOf(content, unit.inventory[index]);
  if (!use) return [];
  return livingUnits(state)
    .filter((other) => {
      if (other.id === unit.id) return use.range[0] === 0 && itemHelps(use, other);
      if (isHostile(unit.team, other.team)) return false;
      const d = spanDistance(at, unit.size, other.position, other.size);
      return d >= Math.max(1, use.range[0]) && d <= use.range[1] && itemHelps(use, other);
    })
    // A própria unidade primeiro: é o uso mais comum, e o que Enter confirma.
    .sort((a, b) => (a.id === unit.id ? -1 : b.id === unit.id ? 1 : a.id - b.id))
    .map((u) => u.id);
}

export interface SkirmishApi extends SkirmishView {
  moveCursor(delta: Vec2): void;
  setCursor(at: Vec2): void;
  confirm(): void;
  cancel(): void;
  chooseMenu(option: Exclude<MenuOption, 'voltar'>): void;
  /** Executa uma ordem do menu de campo. */
  chooseField(option: FieldOption): void;
  /** Abre o menu de campo sem passar pelo cursor. */
  openFieldMenu(): void;
  /**
   * Escolhe o item da bolsa: bebe se for consumível, equipa se for arma.
   *
   * Uma escolha, dois desfechos, porque para o jogador é um gesto só — apontar
   * o que quer da bolsa. Beber encerra o turno; equipar é de graça e devolve ao
   * menu de ação.
   */
  chooseItem(index: number): void;
  setMenuIndex(index: number): void;
  /**
   * Passa um item de uma bolsa para a outra, no rascunho da troca.
   *
   * Nada é gravado aqui: `finishTrade` é que leva o resultado ao motor.
   */
  moveTradeItem(side: 'propria' | 'alheia', index: number): void;
  /** Muda de coluna na tela de troca. */
  setTradeSide(side: 'propria' | 'alheia'): void;
  /** Devolve as duas bolsas ao que eram, sem fechar a tela de troca. */
  resetTrade(): void;
  /**
   * Fecha a troca. Encerra o turno se alguma coisa mudou de dono; se nada
   * mudou, é como se o jogador tivesse desistido, e a unidade continua livre.
   */
  finishTrade(): void;
  cycleTarget(step: number): void;
  selectTarget(targetId: number): void;
  cycleAlly(direction?: 1 | -1): void;
  /**
   * Mostra ou esconde o alcance de UM inimigo.
   *
   * O gesto é o do Fire Emblem: apontar para o inimigo e confirmar. Confirmar
   * de novo apaga. Como lá, é um interruptor por unidade — o jogador acende os
   * dois ou três que estão no caminho e deixa o resto do exército quieto, em
   * vez de pintar meio mapa de uma vez.
   */
  toggleThreat(unitId: number): void;
  /** O mesmo, para quem estiver debaixo do cursor. */
  toggleThreatAtCursor(): void;
  /** Acende ou apaga o alcance do exército inimigo inteiro. */
  toggleAllThreat(): void;
  /** Apaga tudo o que estiver aceso. */
  clearThreat(): void;
  finishPhase(): void;
  reset(): void;
  /**
   * Retoma uma partida gravada, de onde ela parou.
   *
   * Diferente de `reset`, que volta ao tabuleiro recém-montado: aqui o estado
   * vem de fora, com as unidades onde estavam e o turno em que estavam. Ver
   * `game/saves.ts`.
   */
  loadState(next: MatchState): void;
  setPlayMode(mode: 'vs_cpu' | 'hotseat'): void;
  setDifficulty(diff: AiDifficulty): void;
  setCpuSpeed(speed: 'normal' | 'rapido'): void;
  /** Liga e desliga a batalha automática do lado do jogador. */
  setAutoBattle(on: boolean): void;
  /**
   * Uma unidade só joga por si, agora.
   *
   * Diferente da batalha automática, que segue de turno em turno: esta é uma
   * jogada, escolhida pela IA para a unidade selecionada, e o comando volta ao
   * jogador em seguida.
   */
  autoActUnit(unitId: number): void;
}

export interface SkirmishEvents {
  onEvents?(events: readonly Event[], before: MatchState, after: MatchState): void;
  onInvalid?(message: string): void;
  onCursor?(): void;
  /** Uma escolha avançou: unidade selecionada, destino fixado, alvo mirado. */
  onConfirm?(): void;
  /** Uma escolha foi desfeita — voltar do menu, largar a unidade. */
  onCancel?(): void;
  /** Leva a unidade até o destino escolhido, antes de a jogada valer. */
  onPreviewMove?(unitId: number, path: readonly Vec2[]): void;
  /** Desfaz a prévia: a unidade volta para a casa de origem. */
  onCancelPreview?(): void;
  /**
   * A prévia virou jogada: o desenho e o estado voltam a concordar.
   *
   * Só as ações que gastam o turno passam por aqui. Trocar de arma não gasta, e
   * confirmar a prévia nela fazia a unidade reaparecer na casa de origem no
   * instante seguinte — o estado ainda a colocava lá, e sem prévia nada mais
   * segurava o desenho adiantado.
   */
  onCommitPreview?(): void;
  onMoveAnimation?(unitId: number, path: readonly Vec2[]): Promise<void>;
  onCombatAnimation?(event: Extract<Event, { type: 'combate' }>, before: MatchState): Promise<void>;
  onLevelUpAnimation?(
    event: Extract<Event, { type: 'subiu_nivel' }>,
    before: MatchState,
    after: MatchState,
  ): Promise<void>;
  onOpenOptions?(): void;
  /**
   * Câmera, som e tela cheia moram na tela, e não no motor da partida.
   *
   * O menu de campo sabe QUE existem — é ele que as oferece ao controle e ao
   * teclado — mas quem as executa é quem as guarda. `step` percorre a roda da
   * câmera nos dois sentidos.
   */
  onCycleZoom?(step: 1 | -1): void;
  onToggleSound?(): void;
  onToggleFullscreen?(): void;
}

/**
 * Os ajustes que pertencem à MONTAGEM da partida, e não ao curso dela.
 *
 * A dificuldade migrou para cá porque deixou de ser só o juízo da IA: agora
 * ela decide quantos inimigos entram e de onde eles saem, e isso é decidido
 * antes de a primeira peça se mover. Quem monta o tabuleiro é a tela, então é
 * a tela que guarda a escolha — o gancho só a repassa à IA e a devolve à
 * interface.
 */
export interface SkirmishConfig {
  readonly difficulty: AiDifficulty;
  setDifficulty(diff: AiDifficulty): void;
  readonly bannerActive?: boolean;
}

export function useSkirmish(
  initial: MatchState,
  content: Content,
  hooks: SkirmishEvents = {},
  config?: SkirmishConfig,
): SkirmishApi {
  const [state, setState] = useState(initial);
  const stateRef = useRef(state);
  stateRef.current = state;

  const contentRef = useRef(content);
  contentRef.current = content;

  const [selection, setSelection] = useState<Selection>({ mode: 'livre' });
  const [cursor, setCursorAt] = useState<Vec2>(initial.units[0]?.position ?? { x: 0, y: 0 });
  const [log, setLog] = useState<readonly string[]>([]);
  const [playMode, setPlayMode] = useState<'vs_cpu' | 'hotseat'>('vs_cpu');
  const [ownDifficulty, setOwnDifficulty] = useState<AiDifficulty>('normal');
  // Controlada pela tela quando ela quer decidir; do gancho quando não quer —
  // o editor e os testes montam escaramuças sem passar configuração nenhuma.
  const difficulty = config?.difficulty ?? ownDifficulty;
  const setDifficulty = config?.setDifficulty ?? setOwnDifficulty;
  const [cpuSpeed, setCpuSpeed] = useState<'normal' | 'rapido'>('normal');
  const [isCpuActing, setIsCpuActing] = useState(false);
  const [autoBattle, setAutoBattle] = useState(false);
  /*
   * Quais inimigos estão com o alcance à mostra.
   *
   * Só os ids, e não as casas: as casas mudam a cada passo que alguém dá, e
   * guardá-las prontas seria guardar uma resposta que envelhece. O id é o que
   * o jogador escolheu; a mancha se recalcula do estado vivo.
   */
  const [threatIds, setThreatIds] = useState<ReadonlySet<number>>(() => new Set<number>());
  /** O interruptor geral, à parte da lista: acende quem chegar depois também. */
  const [threatAll, setThreatAll] = useState(false);
  const [menuIndex, setMenuIndex] = useState(0);
  const cpuRunningRef = useRef(false);
  const bannerActiveRef = useRef(config?.bannerActive ?? false);
  bannerActiveRef.current = config?.bannerActive ?? false;
  // O laço automático é montado uma vez e não enxerga o estado que muda; é por
  // esta referência que desligar a batalha automática o interrompe no meio.
  const autoBattleRef = useRef(autoBattle);
  autoBattleRef.current = autoBattle;
  const hooksRef = useRef(hooks);
  hooksRef.current = hooks;

  // Quem a CPU move. O inimigo sempre; o aliado é uma unidade de IA que luta ao
  // seu lado — o mapa pode declarar um, e ele age na própria fase.
  const cpuTeams: readonly Team[] = ['inimigo', 'aliado'];
  const cpuPhase =
    playMode === 'vs_cpu' && cpuTeams.includes(state.phase) ? state.phase : null;
  const isCpuTurn = cpuPhase !== null && state.status === 'em_andamento';
  const isCpuTurnRef = useRef(isCpuTurn);
  isCpuTurnRef.current = isCpuTurn;

  /*
   * A fase que a máquina joga — do inimigo ou, na batalha automática, a sua.
   *
   * O laço é o mesmo nos dois casos: `chooseAiAction` não pergunta de que lado
   * a unidade está, ela olha para quem é hostil a ela. Separar em dois laços
   * daria duas cópias da mesma espera, do mesmo cancelamento e do mesmo
   * encerramento de fase — e é justamente aí que os dois caminhos divergiriam
   * com o tempo.
   */
  const autoPhase =
    cpuPhase ?? (autoBattle && state.phase === 'jogador' ? state.phase : null);
  const isAutoTurn = autoPhase !== null && state.status === 'em_andamento';
  const isAutoTurnRef = useRef(isAutoTurn);
  isAutoTurnRef.current = isAutoTurn;
  const cpuPhaseRef = useRef(autoPhase);
  cpuPhaseRef.current = autoPhase;

  useEffect(() => {
    stateRef.current = initial;
    setState(initial);
    setSelection({ mode: 'livre' });
    setCursorAt(initial.units[0]?.position ?? { x: 0, y: 0 });
    setIsCpuActing(false);
    setLog([]);
    // Tabuleiro novo, exército novo: os ids marcados no anterior apontariam
    // para gente que não é a mesma.
    setThreatIds(new Set());
    setThreatAll(false);
  }, [initial]);

  const selectedUnit = useMemo(() => {
    if (selection.mode === 'livre' || selection.mode === 'campo') return null;
    return unitById(state, selection.unitId) ?? null;
  }, [state, selection]);

  // --- alcances -----------------------------------------------------------

  const ranges = useMemo(() => {
    if (!selectedUnit) return { move: [] as Vec2[], attack: [] as Vec2[], range: null };

    const occupancy = buildOccupancy(state.units);
    const movement = content.classes.get(selectedUnit.classId)?.move ?? 5;
    const range = reachableTiles(
      state.map, content, occupancy, selectedUnit.position, movement,
      selectedUnit.team, selectedUnit.size,
    );
    const move = standableTiles(range, occupancy, selectedUnit.id, selectedUnit.size);

    const weapon = weaponOf(content, selectedUnit);
    const attack = weapon
      ? attackableTiles(
          range, occupancy, selectedUnit.id, weapon.range[0], weapon.range[1], selectedUnit.size,
        ).filter((t) => !move.some((m) => samePosition(m, t)))
      : [];

    return { move, attack, range };
  }, [state, content, selectedUnit]);

  const path = useMemo(() => {
    if (selection.mode !== 'movendo' || !ranges.range) return [];
    return pathTo(ranges.range, cursor) ?? [];
  }, [selection, ranges, cursor]);

  // --- alcance inimigo ----------------------------------------------------

  /*
   * Quem está do outro lado AGORA.
   *
   * Sai da fase, e não da equipe "inimigo", porque no hotseat os dois lados
   * são jogados por gente: na vez do vermelho, quem ameaça é o azul. A mesma
   * conta serve aos dois, e é por isso que o rótulo do menu fala de "alcance
   * inimigo" e não de "alcance do time vermelho".
   */
  const hostilesNow = useMemo(
    () => livingUnits(state).filter((u) => isHostile(state.phase, u.team)),
    [state],
  );

  const threatUnits = useMemo(() => {
    /*
     * Enquanto a máquina joga, não se mostra nada.
     *
     * A pergunta "onde é seguro parar?" é da vez do jogador. Respondê-la no
     * meio da jogada alheia — ou da auto-batalha, que também é a máquina
     * andando com as peças — seria pintar uma mancha que muda a cada passo, e
     * ela custa um Dijkstra por inimigo para ser pintada. As marcas ficam
     * guardadas; a mancha volta quando o comando volta.
     */
    if (isCpuTurn || isCpuActing) return [];
    return threatAll ? hostilesNow : hostilesNow.filter((u) => threatIds.has(u.id));
  }, [hostilesNow, isCpuTurn, isCpuActing, threatAll, threatIds]);

  const threat = useMemo(() => {
    if (threatUnits.length === 0) return { tiles: [] as Vec2[], marks: [] as Vec2[] };

    const tiles = hostileReach(
      state.map,
      content,
      threatUnits.map((u) => {
        const weapon = weaponOf(content, u);
        return {
          id: u.id,
          team: u.team,
          position: u.position,
          movement: content.classes.get(u.classId)?.move ?? 5,
          // Desarmado, ou com a arma gasta, o alcance é só o dos pés: min 1 e
          // max 0 não casam com casa nenhuma, e é exatamente o que se quer.
          minRange: weapon ? weapon.range[0] : 1,
          maxRange: weapon ? weapon.range[1] : 0,
          alive: u.alive,
          size: u.size,
        };
      }),
    );

    const marks = threatUnits.flatMap((u) => footprint(u.position, u.size));
    return { tiles, marks };
  }, [state.map, content, threatUnits]);

  // --- alvos --------------------------------------------------------------

  const targetsFrom = useCallback(
    (unit: Unit, at: Vec2, kind: 'atacar' | 'curar'): number[] => {
      const weapon = weaponOf(content, unit);
      if (!weapon) return [];
      if (kind === 'atacar' && weapon.heals) return [];
      if (kind === 'curar' && !weapon.heals) return [];
      // Silenciado, a arma mágica e o cajado não respondem — e o menu não
      // oferece o que o motor vai recusar.
      if (weapon.magic && hasEffect(unit.states, 'sem_magia')) return [];

      return livingUnits(state)
        .filter((other) => {
          if (kind === 'atacar' && other.id === unit.id) return false;
          const pos = other.id === unit.id ? at : other.position;
          const d = spanDistance(at, unit.size, pos, other.size);
          if (d < weapon.range[0] || d > weapon.range[1]) return false;
          const hostile = isHostile(unit.team, other.team);
          if (kind === 'atacar') return hostile;
          // Ferido, ou com condição que este cajado tira.
          return !hostile &&
            (other.currentHp < other.stats.hp || curableBy(other.states, weapon.cures).length > 0);
        })
        .map((u) => u.id)
        .sort((a, b) => a - b);
    },
    [state, content],
  );

  const target = useMemo(() => {
    if (selection.mode !== 'mirando') return null;
    const id = selection.targets[selection.index];
    return id === undefined ? null : unitById(state, id) ?? null;
  }, [selection, state]);

  const plan = useMemo(() => {
    if (selection.mode !== 'mirando' || !selectedUnit || !target || selection.kind !== 'atacar') return null;
    return forecast(
      content,
      combatantOf(state, content, selectedUnit, selection.destination),
      combatantOf(state, content, target, target.position),
      spanDistance(selection.destination, selectedUnit.size, target.position, target.size),
    );
  }, [selection, selectedUnit, target, state, content]);

  // --- aplicar ------------------------------------------------------------

  const perform = useCallback(
    async (action: Action) => {
      const currentState = stateRef.current;
      const currentContent = contentRef.current;
      const result = applyAction(currentState, currentContent, action);
      if (!result.ok) {
        hooksRef.current.onInvalid?.(result.message);
        return false;
      }

      // Trocar de arma é a única ação que não encerra o turno: a unidade
      // continua no meio da jogada, e nem a prévia nem o menu podem ser
      // desmontados por causa dela.
      const endsTurn = action.type !== 'trocar_arma';

      // Se houver caminho de movimento, anima o personagem caminhando
      if ('path' in action && action.path && action.path.length > 1 && hooksRef.current.onMoveAnimation) {
        await hooksRef.current.onMoveAnimation(action.unitId, action.path);
      }

      const after = settle(result.state);

      // Se for combate, aguarda a animação de combate lateral
      const combatEvent = result.events.find(
        (e): e is Extract<Event, { type: 'combate' }> => e.type === 'combate',
      );
      if (combatEvent && hooksRef.current.onCombatAnimation) {
        await hooksRef.current.onCombatAnimation(combatEvent, currentState);
      }

      // Se houver subida de nível, aguarda a animação e confirmação da tela de subida de nível
      const levelEvent = result.events.find(
        (e): e is Extract<Event, { type: 'subiu_nivel' }> => e.type === 'subiu_nivel',
      );
      if (levelEvent && hooksRef.current.onLevelUpAnimation) {
        await hooksRef.current.onLevelUpAnimation(levelEvent, currentState, after);
      }

      if (endsTurn) hooksRef.current.onCommitPreview?.();
      hooksRef.current.onEvents?.(result.events, currentState, after);
      stateRef.current = after;
      setState(after);
      if (endsTurn) setSelection({ mode: 'livre' });
      return true;
    },
    [],
  );

  const finishPhase = useCallback(() => {
    const before = stateRef.current;
    const result = endPhase(before, contentRef.current);
    // O começo de fase também acontece coisas — o veneno cobra, a paralisia
    // passa —, e a tela precisa contar. Antes estes eventos morriam aqui.
    hooksRef.current.onEvents?.(result.events, before, result.state);
    stateRef.current = result.state;
    setState(result.state);
    setSelection({ mode: 'livre' });
    const next = livingUnits(result.state, result.state.phase)[0];
    if (next) setCursorAt(next.position);
    setLog((l) => [...l, `— fase ${result.state.phase}, turno ${result.state.turn} —`]);
  }, []);

  /**
   * A fase vira sozinha quando ninguém mais pode agir.
   *
   * Mover a última peça e ainda ter de avisar que acabou é trabalho que o jogo
   * já sabe fazer: `phaseIsOver` conhece a resposta no instante em que a
   * jogada é aplicada. O jogador continua podendo encerrar antes, pelo menu de
   * campo ou pelo atalho — o automático só cobre o caso em que não há mais
   * escolha nenhuma a fazer.
   *
   * A espera curta é de propósito: sem ela a faixa da fase seguinte entra em
   * cima da animação da última ação. A fase da CPU não passa por aqui, porque
   * o próprio laço da IA a encerra quando fica sem unidade para mover.
   */
  useEffect(() => {
    if (state.status !== 'em_andamento') return;
    if (isCpuTurn || isCpuActing) return;
    if (selection.mode !== 'livre') return;
    if (!phaseIsOver(state)) return;

    const id = window.setTimeout(() => finishPhase(), AUTO_END_PHASE_MS);
    return () => window.clearTimeout(id);
  }, [state, selection.mode, isCpuTurn, isCpuActing, finishPhase]);

  // --- automação da cpu ---------------------------------------------------

  useEffect(() => {
    if (!isAutoTurn || state.status !== 'em_andamento' || cpuRunningRef.current) return;

    let cancelled = false;
    cpuRunningRef.current = true;

    const runCpuTurn = async () => {
      const stepDelay = cpuSpeed === 'rapido' ? 200 : 500;
      const finishDelay = cpuSpeed === 'rapido' ? 150 : 350;

      try {
        // Aguarda a faixa de anúncio de início de fase terminar antes de agir
        while (bannerActiveRef.current) {
          await new Promise((r) => setTimeout(r, 60));
          if (cancelled || !isAutoTurnRef.current) return;
        }

        setIsCpuActing(true);

        // Pausa antes do primeiro movimento da CPU para o jogador acompanhar
        await new Promise((r) => setTimeout(r, stepDelay));

        while (
          !cancelled &&
          isAutoTurnRef.current &&
          stateRef.current.phase === cpuPhaseRef.current &&
          stateRef.current.status === 'em_andamento'
        ) {
          // Desligar a batalha automática devolve o comando na hora, e não no
          // fim do turno: o jogador que aperta o botão quer jogar a próxima
          // peça, não assistir às sete restantes.
          if (stateRef.current.phase === 'jogador' && !autoBattleRef.current) break;
          const actor = nextAiActor(stateRef.current, stateRef.current.phase);
          if (!actor) {
            // Todas as unidades da CPU agiram
            break;
          }

          // Move o cursor até a unidade que vai agir
          setCursorAt(actor.position);
          await new Promise((r) => setTimeout(r, stepDelay));
          if (cancelled || !isAutoTurnRef.current) break;

          const action = chooseAiAction(stateRef.current, contentRef.current, actor, { difficulty });

          // Mostra o alvo / destino no cursor antes de agir
          if ('targetId' in action && action.targetId) {
            const targetU = unitById(stateRef.current, action.targetId);
            if (targetU) setCursorAt(targetU.position);
          } else if ('path' in action && action.path && action.path.length > 0) {
            setCursorAt(action.path[action.path.length - 1]!);
          }

          const success = await perform(action);
          if (!success || cancelled || !isAutoTurnRef.current) break;

          // Pausa entre as ações de cada unidade
          await new Promise((r) => setTimeout(r, finishDelay));
        }

        // A fase do jogador só é encerrada pelo laço se ele de fato jogou até
        // o fim: desligar a automática no meio do turno tem de deixar as
        // unidades que sobraram na mão de quem desligou.
        const stillAuto =
          stateRef.current.phase !== 'jogador' || autoBattleRef.current;
        if (
          !cancelled &&
          stillAuto &&
          isAutoTurnRef.current &&
          stateRef.current.phase === cpuPhaseRef.current &&
          stateRef.current.status === 'em_andamento'
        ) {
          finishPhase();
        }
      } finally {
        cpuRunningRef.current = false;
        setIsCpuActing(false);
      }
    };

    void runCpuTurn();

    return () => {
      cancelled = true;
      cpuRunningRef.current = false;
      setIsCpuActing(false);
    };
    // A fase entra nas dependências porque com a batalha automática ligada os
    // dois lados são jogados pela máquina: `isAutoTurn` fica verdadeiro de uma
    // ponta à outra do turno, e sem a virada de fase aqui o laço terminaria o
    // seu lado e nunca acordaria para o do adversário.
  }, [isAutoTurn, state.phase, difficulty, cpuSpeed, perform, finishPhase]);

  // --- menu de ações ------------------------------------------------------

  const menuUnit = selection.mode === 'menu' ? selectedUnit : null;
  const canAttack = useMemo(
    () =>
      menuUnit
        ? targetsFrom(menuUnit, (selection as Extract<Selection, { mode: 'menu' }>).destination, 'atacar').length > 0
        : false,
    [menuUnit, selection, targetsFrom],
  );
  const canHeal = useMemo(
    () =>
      menuUnit
        ? targetsFrom(menuUnit, (selection as Extract<Selection, { mode: 'menu' }>).destination, 'curar').length > 0
        : false,
    [menuUnit, selection, targetsFrom],
  );

  /**
   * Com quem dá para trocar itens da casa onde a unidade parou.
   *
   * Só a própria equipe, e só quem está encostado — a troca é de mão em mão.
   * Quem já agiu conta: passar a poção para o companheiro que ainda vai jogar é
   * metade da razão de a troca existir.
   */
  const tradePartners = useMemo<readonly number[]>(() => {
    if (selection.mode !== 'menu' || !menuUnit) return [];
    const at = (selection as Extract<Selection, { mode: 'menu' }>).destination;
    return livingUnits(state, menuUnit.team)
      .filter(
        (other) =>
          other.id !== menuUnit.id &&
          spanDistance(at, menuUnit.size, other.position, other.size) === 1,
      )
      .map((u) => u.id)
      .sort((a, b) => a - b);
  }, [selection, menuUnit, state]);

  /**
   * Com quem, do lado de lá, dá para conversar daqui de onde a unidade parou.
   *
   * A mesma pergunta que o motor faz (`canTalkTo`), feita pela tela: o menu
   * não oferece "Conversar" a quem não tem com quem, e não oferece a conversa
   * a quem o capítulo não escolheu para fazê-la.
   */
  const talkTargets = useMemo<readonly number[]>(() => {
    if (selection.mode !== 'menu' || !menuUnit) return [];
    const at = (selection as Extract<Selection, { mode: 'menu' }>).destination;
    return livingUnits(state)
      .filter(
        (other) =>
          canTalkTo(menuUnit, other) &&
          spanDistance(at, menuUnit.size, other.position, other.size) === 1,
      )
      .map((u) => u.id)
      .sort((a, b) => a - b);
  }, [selection, menuUnit, state]);

  /**
   * A casa onde a unidade parou tem baú que ela consegue abrir?
   *
   * As duas coisas juntas, porque oferecer "Abrir baú" a quem não tem chave
   * nem é ladrão é oferecer uma ordem que o motor vai recusar — e um menu que
   * oferece o que não funciona é pior do que um menu curto.
   */
  const chestHere = useMemo(() => {
    if (selection.mode !== 'menu' || !menuUnit) return false;
    const at = (selection as Extract<Selection, { mode: 'menu' }>).destination;
    const chest = chestAt(state, at);
    if (!chest) return false;
    return chest.locked === false || chestOpener(content, menuUnit).can;
  }, [selection, menuUnit, state, content]);

  const menuOptions = useMemo<readonly MenuOption[]>(() => {
    if (selection.mode !== 'menu') return [];
    const opts: MenuOption[] = [];
    // Primeiro de todos, e de propósito: a conversa é a única ordem que se
    // perde para sempre se a pessoa atacar por engano quem ia recrutar.
    if (talkTargets.length > 0) opts.push('conversar');
    if (canAttack) opts.push('atacar');
    if (canHeal) opts.push('curar');
    // Antes de tudo o que se pode fazer com a bolsa: abrir o baú é a razão de
    // a unidade ter vindo até esta casa.
    if (chestHere) opts.push('bau');
    // A bolsa abre com qualquer coisa dentro: mesmo com um item só, ele pode
    // ser a poção que salva a unidade neste turno.
    if ((menuUnit?.inventory.length ?? 0) > 0) opts.push('itens');
    // A troca aparece com um companheiro ao lado, tenha ele bolsa ou não: dar
    // um item a quem está de mãos vazias é uma troca tão legítima quanto pegar.
    if (tradePartners.length > 0) opts.push('trocar');
    opts.push('esperar');
    // Deixar a máquina jogar esta peça. Fica acima de "voltar" porque é ordem,
    // e não desistência — e abaixo de "esperar" porque é a menos comum das duas.
    opts.push('auto');
    opts.push('voltar');
    return opts;
  }, [selection.mode, canAttack, canHeal, chestHere, menuUnit, tradePartners, talkTargets]);

  useEffect(() => {
    if (selection.mode === 'menu') {
      setMenuIndex(0);
    }
  }, [selection.mode]);

  // --- comandos -----------------------------------------------------------

  const setCursor = useCallback(
    (at: Vec2) => {
      if (isCpuTurn || isCpuActing) return;
      if (at.x < 0 || at.y < 0 || at.x >= state.map.width || at.y >= state.map.height) return;
      setCursorAt((current) => {
        if (samePosition(current, at)) return current;
        hooksRef.current.onCursor?.();
        return at;
      });
    },
    [state.map, isCpuTurn, isCpuActing],
  );

  const cycleTarget = useCallback((step: number) => {
    if (isCpuTurn || isCpuActing) return;
    setSelection((current) => {
      if (current.mode !== 'mirando' || current.targets.length === 0) return current;
      const index = (current.index + step + current.targets.length) % current.targets.length;
      if (index !== current.index) hooksRef.current.onCursor?.();
      return { ...current, index };
    });
  }, [isCpuTurn, isCpuActing]);

  const selectTarget = useCallback((targetId: number) => {
    if (isCpuTurn || isCpuActing) return;
    setSelection((current) => {
      if (current.mode !== 'mirando') return current;
      const index = current.targets.indexOf(targetId);
      if (index === -1 || index === current.index) return current;
      hooksRef.current.onCursor?.();
      return { ...current, index };
    });
  }, [isCpuTurn, isCpuActing]);

  /*
   * Os mortos saem da lista sozinhos.
   *
   * Sem esta limpeza o contador do menu continuaria contando o inimigo que
   * acabou de cair, e o id vago ainda poderia casar com outra unidade se a
   * partida fosse reiniciada por baixo. A mancha já ignoraria o morto — o
   * cálculo lê o estado vivo —, mas o que a interface DIZ também tem de ser
   * verdade.
   */
  useEffect(() => {
    setThreatIds((current) => {
      if (current.size === 0) return current;
      const next = new Set([...current].filter((id) => unitById(state, id)?.alive === true));
      return next.size === current.size ? current : next;
    });
  }, [state]);

  const toggleThreat = useCallback(
    (unitId: number) => {
      if (isCpuTurn || isCpuActing) return;
      const unit = unitById(state, unitId);
      if (!unit?.alive || !isHostile(state.phase, unit.team)) {
        hooksRef.current.onInvalid?.('O alcance é de quem joga contra você.');
        return;
      }

      /*
       * Apagar UM com o interruptor geral aceso não pode apagar o resto.
       *
       * Por isso o geral vira lista aqui: o jogador vê meio exército pintado,
       * clica no arqueiro para tirá-lo da conta e espera perder o arqueiro —
       * não o mapa inteiro.
       */
      const base = threatAll ? new Set(hostilesNow.map((u) => u.id)) : new Set(threatIds);
      const lighting = !base.has(unitId);
      if (lighting) base.add(unitId);
      else base.delete(unitId);

      // O aviso sai daqui, e não de dentro de um atualizador de estado: o
      // React pode reexecutar o atualizador, e som tocado duas vezes é o que
      // sobra disso. Mesmo motivo de `cancel`.
      if (lighting) hooksRef.current.onConfirm?.();
      else hooksRef.current.onCancel?.();

      setThreatAll(false);
      setThreatIds(base);
    },
    [state, isCpuTurn, isCpuActing, threatAll, threatIds, hostilesNow],
  );

  const toggleThreatAtCursor = useCallback(() => {
    const unit = unitAt(state, cursor);
    if (!unit) {
      hooksRef.current.onInvalid?.('Aponte para um inimigo para ver o alcance dele.');
      return;
    }
    toggleThreat(unit.id);
  }, [state, cursor, toggleThreat]);

  const toggleAllThreat = useCallback(() => {
    // Um interruptor só: se há QUALQUER coisa acesa — o geral ou três nomes
    // escolhidos a dedo —, o gesto apaga. É o que se espera de um botão que
    // se aperta para limpar a tela.
    const lighting = !(threatAll || threatIds.size > 0);
    if (lighting) hooksRef.current.onConfirm?.();
    else hooksRef.current.onCancel?.();
    setThreatAll(lighting);
    setThreatIds(new Set());
  }, [threatAll, threatIds]);

  const clearThreat = useCallback(() => {
    setThreatAll(false);
    setThreatIds(new Set());
  }, []);

  const moveCursor = useCallback(
    (delta: Vec2) => {
      // Navegar o menu de campo continua valendo com a máquina jogando: é por
      // ele que se desliga a batalha automática.
      if ((isCpuTurn || isCpuActing) && selection.mode !== 'campo') return;

      if (selection.mode === 'mirando') {
        if (delta.x === 0 && delta.y === 0) return;
        const targets = selection.targets;
        if (targets.length <= 1) return;

        const currentTargetId = targets[selection.index];
        const currentTargetUnit = currentTargetId !== undefined ? unitById(state, currentTargetId) : null;

        if (currentTargetUnit) {
          const currPos = currentTargetUnit.position;
          let bestIndex = -1;
          let bestScore = -Infinity;

          for (let i = 0; i < targets.length; i++) {
            if (i === selection.index) continue;
            const other = unitById(state, targets[i]!);
            if (!other) continue;
            const vx = other.position.x - currPos.x;
            const vy = other.position.y - currPos.y;
            const dot = vx * delta.x + vy * delta.y;
            if (dot > 0) {
              const dist = Math.hypot(vx, vy);
              const score = dot / dist;
              if (score > bestScore) {
                bestScore = score;
                bestIndex = i;
              }
            }
          }

          if (bestIndex !== -1) {
            hooksRef.current.onCursor?.();
            setSelection((c) => (c.mode === 'mirando' ? { ...c, index: bestIndex } : c));
            return;
          }
        }

        // Se não houver alvo no quadrante apontado, cicla para frente ou trás
        const step = delta.x > 0 || delta.y > 0 ? 1 : -1;
        cycleTarget(step);
        return;
      }

      if (selection.mode === 'campo') {
        // Esquerda e direita mudam o VALOR da linha, quando ela tem valor —
        // é a câmera, que percorre os cinco afastamentos nos dois sentidos.
        if (delta.x !== 0) {
          const current = FIELD_OPTIONS[selection.index];
          if (current && FIELD_VALUE_OPTIONS.has(current)) {
            hooksRef.current.onCursor?.();
            hooksRef.current.onCycleZoom?.(delta.x > 0 ? 1 : -1);
          }
          return;
        }
        if (delta.y === 0) return;
        hooksRef.current.onCursor?.();
        setSelection((current) =>
          current.mode === 'campo'
            ? {
                ...current,
                index:
                  (current.index + (delta.y > 0 ? 1 : -1) + FIELD_OPTIONS.length) %
                  FIELD_OPTIONS.length,
              }
            : current,
        );
        return;
      }

      if (selection.mode === 'menu') {
        if (delta.y !== 0 && menuOptions.length > 0) {
          hooksRef.current.onCursor?.();
          setMenuIndex((curr) => (curr + (delta.y > 0 ? 1 : -1) + menuOptions.length) % menuOptions.length);
        }
        return;
      }

      if (selection.mode === 'trocando') {
        const bag = selection.side === 'propria' ? selection.mine : selection.theirs;
        if (delta.x !== 0) {
          const side = selection.side === 'propria' ? 'alheia' : 'propria';
          hooksRef.current.onCursor?.();
          setSelection((current) =>
            current.mode === 'trocando'
              ? {
                  ...current,
                  side,
                  index: Math.min(
                    current.index,
                    Math.max(0, (side === 'propria' ? current.mine : current.theirs).length - 1),
                  ),
                }
              : current,
          );
          return;
        }
        if (delta.y === 0 || bag.length === 0) return;
        hooksRef.current.onCursor?.();
        setSelection((current) =>
          current.mode === 'trocando'
            ? {
                ...current,
                index:
                  (current.index +
                    (delta.y > 0 ? 1 : -1) +
                    (current.side === 'propria' ? current.mine : current.theirs).length) %
                  Math.max(1, (current.side === 'propria' ? current.mine : current.theirs).length),
              }
            : current,
        );
        return;
      }

      if (selection.mode === 'itens') {
        if (delta.y === 0) return;
        const size = selectedUnit?.inventory.length ?? 0;
        if (size === 0) return;
        hooksRef.current.onCursor?.();
        setSelection((current) =>
          current.mode === 'itens'
            ? { ...current, index: (current.index + (delta.y > 0 ? 1 : -1) + size) % size }
            : current,
        );
        return;
      }

      setCursor({ x: cursor.x + delta.x, y: cursor.y + delta.y });
    },
    [cursor, selection, state, isCpuTurn, isCpuActing, setCursor, menuOptions.length, selectedUnit, cycleTarget],
  );

  const cycleAlly = useCallback(
    (direction: 1 | -1 = 1) => {
      if (isCpuTurn || isCpuActing || selection.mode !== 'livre') return;
      const readyUnits = livingUnits(state, state.phase).filter((u) => !u.hasActed);
      if (readyUnits.length === 0) return;

      const sorted = [...readyUnits].sort((a, b) => a.id - b.id);
      // Pela casa coberta: o cursor pode estar sobre a cauda de um corpo grande.
      const currentIndex = sorted.findIndex((u) => covers(u.position, u.size, cursor));

      let nextIndex = 0;
      if (currentIndex !== -1) {
        nextIndex = (currentIndex + direction + sorted.length) % sorted.length;
      }

      const nextUnit = sorted[nextIndex];
      if (nextUnit) {
        hooksRef.current.onCursor?.();
        setCursorAt(nextUnit.position);
      }
    },
    [state, isCpuTurn, isCpuActing, selection.mode, cursor],
  );

  const cancel = useCallback(() => {
    if ((isCpuTurn || isCpuActing) && selection.mode !== 'campo') return;

    // Os avisos saem daqui, e não de dentro do atualizador de estado: o React
    // pode reexecutar um atualizador, e som tocado duas vezes — ou uma prévia
    // desfeita duas vezes — seria o resultado.
    const current = selection;
    if (current.mode === 'livre') return;

    hooksRef.current.onCancel?.();
    if (current.mode === 'menu') hooksRef.current.onCancelPreview?.();

    switch (current.mode) {
      case 'campo':
        setSelection({ mode: 'livre' });
        return;
      // Desistir do alvo do item volta à bolsa, com o item ainda apontado.
      case 'mirando':
        if (current.kind === 'item') {
          setSelection({
            mode: 'itens',
            unitId: current.unitId,
            destination: current.destination,
            path: current.path,
            index: current.itemIndex ?? 0,
          });
          return;
        }
        setSelection({
          mode: 'menu',
          unitId: current.unitId,
          destination: current.destination,
          path: current.path,
        });
        return;
      // Sair da troca pelo cancelar desfaz o rascunho inteiro: as duas bolsas
      // voltam a ser o que o estado da partida ainda diz que elas são.
      case 'trocando':
      case 'itens':
        setSelection({
          mode: 'menu',
          unitId: current.unitId,
          destination: current.destination,
          path: current.path,
        });
        return;
      case 'menu':
        setSelection({ mode: 'movendo', unitId: current.unitId });
        return;
      case 'movendo':
        setSelection({ mode: 'livre' });
        return;
    }
  }, [selection, isCpuTurn, isCpuActing]);

  /**
   * A IA joga por uma unidade só, desta vez.
   *
   * A prévia é desfeita antes de tudo: o jogador já tinha arrastado a peça até
   * uma casa, e a IA planeja a partir de onde a unidade REALMENTE está. Sem
   * desfazer, o desenho aceitaria o destino escolhido à mão e o motor aplicaria
   * o da máquina — dois lugares diferentes para a mesma unidade.
   */
  const autoActUnit = useCallback(
    (unitId: number) => {
      if (isCpuTurn || isCpuActing) return;
      const unit = unitById(stateRef.current, unitId);
      if (!unit || unit.hasActed || !unit.alive) return;

      hooksRef.current.onConfirm?.();
      hooksRef.current.onCancelPreview?.();
      setSelection({ mode: 'livre' });

      const action = chooseAiAction(stateRef.current, contentRef.current, unit, { difficulty });
      void perform(action);
    },
    [isCpuTurn, isCpuActing, difficulty, perform],
  );

  /**
   * Abre a tela de troca entre a unidade que parou aqui e o companheiro
   * escolhido.
   *
   * Recebe a âncora (quem, de onde, por que caminho) em vez de a ler da
   * seleção porque ela é chamada de dois lugares: do menu, quando há um
   * companheiro só, e da mira, quando o jogador acabou de escolher entre
   * vários. As bolsas saem do estado da partida na hora — o rascunho começa
   * sendo o que as duas realmente carregam.
   */
  const openTrade = useCallback(
    (
      anchor: {
        readonly unitId: number;
        readonly destination: Vec2;
        readonly path: readonly Vec2[];
      },
      partnerId: number,
    ) => {
      const actor = unitById(stateRef.current, anchor.unitId);
      const partner = unitById(stateRef.current, partnerId);
      if (!actor || !partner) {
        hooksRef.current.onInvalid?.('Ninguém da sua equipe ao lado para trocar.');
        return;
      }
      hooksRef.current.onConfirm?.();
      setSelection({
        mode: 'trocando',
        unitId: anchor.unitId,
        destination: anchor.destination,
        path: anchor.path,
        targetId: partner.id,
        side: 'propria',
        index: 0,
        mine: actor.inventory,
        theirs: partner.inventory,
      });
    },
    [],
  );

  const chooseMenu = useCallback(
    (option: Exclude<MenuOption, 'voltar'>) => {
      if (isCpuTurn || isCpuActing || selection.mode !== 'menu' || !selectedUnit) return;

      if (option === 'conversar') {
        if (talkTargets.length === 0) {
          hooksRef.current.onInvalid?.('Ninguém ao lado para conversar.');
          return;
        }
        hooksRef.current.onConfirm?.();
        setSelection({ ...selection, mode: 'mirando', kind: 'conversar', targets: talkTargets, index: 0 });
        return;
      }

      if (option === 'auto') {
        autoActUnit(selection.unitId);
        return;
      }

      if (option === 'bau') {
        perform({ type: 'abrir_bau', unitId: selection.unitId, path: selection.path });
        return;
      }

      if (option === 'esperar') {
        perform({ type: 'mover', unitId: selection.unitId, path: selection.path });
        return;
      }

      if (option === 'trocar') {
        if (tradePartners.length === 0) {
          hooksRef.current.onInvalid?.('Ninguém da sua equipe ao lado para trocar.');
          return;
        }
        /*
         * Dois companheiros encostados são duas trocas diferentes.
         *
         * Com um só, abrir a tela direto: perguntar "com quem?" numa lista de
         * um nome é um passo a mais para nada. Com dois ou mais, a mira — a
         * mesma de atacar e de conversar, com o cursor pousando em cada um.
         */
        if (tradePartners.length > 1) {
          hooksRef.current.onConfirm?.();
          setSelection({ ...selection, mode: 'mirando', kind: 'trocar', targets: tradePartners, index: 0 });
          return;
        }
        openTrade(selection, tradePartners[0]!);
        return;
      }

      if (option === 'itens') {
        hooksRef.current.onConfirm?.();
        setSelection({
          mode: 'itens',
          unitId: selection.unitId,
          destination: selection.destination,
          path: selection.path,
          index: selectedUnit.equipped,
        });
        return;
      }

      const targets = targetsFrom(selectedUnit, selection.destination, option);
      if (targets.length === 0) {
        hooksRef.current.onInvalid?.(
          option === 'atacar' ? 'Nenhum inimigo ao alcance daqui.' : 'Ninguém ferido ao alcance.',
        );
        return;
      }
      hooksRef.current.onConfirm?.();
      setSelection({ ...selection, mode: 'mirando', kind: option, targets, index: 0 });
    },
    [
      selection, selectedUnit, targetsFrom, isCpuTurn, isCpuActing, perform, tradePartners,
      talkTargets, autoActUnit, openTrade,
    ],
  );

  /**
   * A bolsa: beber o consumível, ou equipar a arma.
   *
   * Trocar de arma é ação livre — não gasta o turno — e por isso devolve ao
   * menu de ação: é assim que se equipa a lança para o cavaleiro e ataca no
   * mesmo turno. Beber a poção é o contrário: cura ali onde a unidade parou,
   * encerra a jogada e deixa a arma equipada como estava.
   */
  const chooseItem = useCallback(
    (index: number) => {
      if (isCpuTurn || isCpuActing || selection.mode !== 'itens' || !selectedUnit) return;

      const item = selectedUnit.inventory[index];
      if (!item) return;
      const use = itemUseOf(contentRef.current, item);

      if (use) {
        const targets = itemTargets(state, contentRef.current, selectedUnit, selection.destination, index);
        if (targets.length === 0) {
          hooksRef.current.onInvalid?.(
            use.cures.length > 0
              ? 'Ninguém aqui precisa disto — nem PV a recuperar, nem condição que o item cure.'
              : `${selectedUnit.name} está com os PV cheios, e ninguém ao lado precisa.`,
          );
          return;
        }
        hooksRef.current.onConfirm?.();
        // Um alvo só — quase sempre a própria unidade — dispensa a mira.
        if (targets.length === 1) {
          void perform({
            type: 'usar_item',
            unitId: selection.unitId,
            path: selection.path,
            index,
            ...(targets[0] !== selectedUnit.id ? { targetId: targets[0] } : {}),
          });
          return;
        }
        setSelection({
          mode: 'mirando',
          unitId: selection.unitId,
          destination: selection.destination,
          path: selection.path,
          kind: 'item',
          targets,
          index: 0,
          itemIndex: index,
        });
        return;
      }

      if (!isWeaponSlot(item)) {
        // Item que não se usa nem se empunha: a chave, que abre o baú pelo menu.
        hooksRef.current.onInvalid?.('Este item se usa sozinho: a chave abre o baú quando a unidade para em cima dele.');
        return;
      }

      void perform({ type: 'trocar_arma', unitId: selection.unitId, index });
      setSelection({
        mode: 'menu',
        unitId: selection.unitId,
        destination: selection.destination,
        path: selection.path,
      });
    },
    [selection, selectedUnit, isCpuTurn, isCpuActing, perform, state],
  );

  /**
   * Passa um item de uma bolsa para a outra, no rascunho.
   *
   * Um gesto só, e ele já sabe para onde vai: o item escolhido na coluna da
   * unidade vai para o companheiro, e o escolhido na coluna dele vem para cá.
   * Uma bolsa cheia recusa — quatro é o teto, e é o motor quem o define.
   */
  const moveTradeItem = useCallback(
    (side: 'propria' | 'alheia', index: number) => {
      if (isCpuTurn || isCpuActing) return;

      setSelection((current) => {
        if (current.mode !== 'trocando') return current;

        const from = side === 'propria' ? current.mine : current.theirs;
        const to = side === 'propria' ? current.theirs : current.mine;
        const item = from[index];
        if (!item) return current;

        if (to.length >= MAX_INVENTORY) {
          hooksRef.current.onInvalid?.(`A outra bolsa já leva ${MAX_INVENTORY} itens.`);
          return current;
        }

        const shrunk = from.filter((_, i) => i !== index);
        const grown = [...to, item];
        const nextIndex = Math.max(0, Math.min(index, shrunk.length - 1));

        return side === 'propria'
          ? { ...current, mine: shrunk, theirs: grown, index: nextIndex }
          : { ...current, theirs: shrunk, mine: grown, index: nextIndex };
      });
      hooksRef.current.onConfirm?.();
    },
    [isCpuTurn, isCpuActing],
  );

  const resetTrade = useCallback(() => {
    if (isCpuTurn || isCpuActing) return;
    setSelection((current) => {
      if (current.mode !== 'trocando') return current;
      const actor = unitById(stateRef.current, current.unitId);
      const partner = unitById(stateRef.current, current.targetId);
      if (!actor || !partner) return current;
      hooksRef.current.onCancel?.();
      return { ...current, mine: actor.inventory, theirs: partner.inventory, index: 0 };
    });
  }, [isCpuTurn, isCpuActing]);

  const setTradeSide = useCallback(
    (side: 'propria' | 'alheia') => {
      if (isCpuTurn || isCpuActing) return;
      setSelection((current) => {
        if (current.mode !== 'trocando' || current.side === side) return current;
        const bag = side === 'propria' ? current.mine : current.theirs;
        return { ...current, side, index: Math.max(0, Math.min(current.index, bag.length - 1)) };
      });
    },
    [isCpuTurn, isCpuActing],
  );

  /**
   * Fecha a troca.
   *
   * Trocar gasta o turno, como no Fire Emblem — mas só se alguma coisa mudou de
   * dono. Abrir a tela, olhar as duas bolsas e fechar não pode custar a jogada
   * da unidade: seria punir quem foi conferir o que o companheiro carrega.
   */
  const finishTrade = useCallback(() => {
    if (isCpuTurn || isCpuActing) return;

    const current = selection;
    if (current.mode !== 'trocando') return;

    const actor = unitById(stateRef.current, current.unitId);
    const partner = unitById(stateRef.current, current.targetId);
    if (!actor || !partner) {
      setSelection({ mode: 'livre' });
      return;
    }

    const untouched =
      current.mine.length === actor.inventory.length &&
      current.theirs.length === partner.inventory.length &&
      current.mine.every((it, i) => it === actor.inventory[i]) &&
      current.theirs.every((it, i) => it === partner.inventory[i]);

    if (untouched) {
      hooksRef.current.onCancel?.();
      setSelection({
        mode: 'menu',
        unitId: current.unitId,
        destination: current.destination,
        path: current.path,
      });
      return;
    }

    hooksRef.current.onConfirm?.();
    void perform({
      type: 'trocar_itens',
      unitId: current.unitId,
      path: current.path,
      targetId: current.targetId,
      actorInventory: current.mine,
      targetInventory: current.theirs,
    });
  }, [selection, isCpuTurn, isCpuActing, perform]);

  /**
   * O menu de campo abre mesmo com a máquina jogando o seu lado.
   *
   * É a saída da batalha automática, e ela precisa existir DENTRO do
   * tabuleiro: em tela cheia não há barra de página para clicar. O menu abre
   * ainda assim; o que estiver barrado — encerrar fase, opções — é recusado
   * uma a uma, em `chooseField`.
   */
  const openFieldMenu = useCallback(() => {
    if (stateRef.current.status !== 'em_andamento') return;
    if (isCpuTurn && !autoBattleRef.current) return;
    hooksRef.current.onConfirm?.();
    setSelection({ mode: 'campo', index: 0 });
  }, [isCpuTurn]);

  const chooseField = useCallback(
    (option: FieldOption) => {
      /*
       * Desligar a batalha automática é a ÚNICA ordem que atravessa a máquina
       * jogando — e tem de atravessar, porque é justamente enquanto ela joga
       * que o jogador quer o comando de volta. Barrar aqui, como as demais,
       * deixava o menu aberto e inerte: o jogo seguia sozinho para sempre.
       */
      if (option === 'auto_fase') {
        hooksRef.current.onConfirm?.();
        setAutoBattle((on) => !on);
        setSelection({ mode: 'livre' });
        return;
      }
      // Câmera, som e tela cheia também: são ajustes de tela, não jogadas, e
      // nada ganha em ficarem indisponíveis durante o turno do adversário.
      if (option === 'camera') {
        hooksRef.current.onCursor?.();
        hooksRef.current.onCycleZoom?.(1);
        return;
      }
      if (option === 'som') {
        hooksRef.current.onConfirm?.();
        hooksRef.current.onToggleSound?.();
        return;
      }
      if (option === 'tela_cheia') {
        hooksRef.current.onConfirm?.();
        hooksRef.current.onToggleFullscreen?.();
        return;
      }

      if (isCpuTurn || isCpuActing) return;
      /*
       * Acender o alcance fecha o menu.
       *
       * Diferente da câmera e do som, que se ajustam olhando para o próprio
       * menu: aqui o resultado está no tabuleiro, e o menu é justamente o que
       * está por cima dele.
       */
      if (option === 'ameaca') {
        toggleAllThreat();
        setSelection({ mode: 'livre' });
        return;
      }
      if (option === 'voltar') {
        hooksRef.current.onCancel?.();
        setSelection({ mode: 'livre' });
        return;
      }
      if (option === 'opcoes') {
        hooksRef.current.onConfirm?.();
        setSelection({ mode: 'livre' });
        hooksRef.current.onOpenOptions?.();
        return;
      }
      hooksRef.current.onConfirm?.();
      finishPhase();
    },
    [isCpuTurn, isCpuActing, finishPhase, toggleAllThreat],
  );

  const confirm = useCallback(() => {
    if (state.status !== 'em_andamento') return;
    // O menu de campo responde sempre; o resto espera a sua vez.
    if ((isCpuTurn || isCpuActing) && selection.mode !== 'campo') return;

    switch (selection.mode) {
      case 'livre': {
        // Pela casa coberta, e não pela âncora: apontar para a cauda do dragão
        // seleciona o dragão.
        const unit = unitAt(state, cursor);
        // Casa vazia abre o menu de campo — é o gesto do Fire Emblem, e é por
        // ele que "encerrar fase" existe dentro do tabuleiro.
        if (!unit) {
          openFieldMenu();
          return;
        }
        /*
         * Confirmar em cima de um hostil acende o alcance DELE.
         *
         * É o gesto do Fire Emblem, e é o que o jogador tenta primeiro:
         * apontar para o inimigo e apertar confirmar. Antes disso a resposta
         * era um "não é sua para comandar" — verdade inútil, porque ninguém
         * apontou para o inimigo querendo comandá-lo. Confirmar de novo apaga.
         */
        if (isHostile(state.phase, unit.team)) {
          toggleThreat(unit.id);
          return;
        }
        if (unit.team !== state.phase || unit.hasActed) {
          hooksRef.current.onInvalid?.(
            unit.team === state.phase
              ? `${unit.name} já agiu neste turno.`
              : `${unit.name} não é sua para comandar.`,
          );
          return;
        }
        hooksRef.current.onConfirm?.();
        setSelection({ mode: 'movendo', unitId: unit.id });
        return;
      }

      case 'campo': {
        const option = FIELD_OPTIONS[selection.index];
        if (option) chooseField(option);
        return;
      }

      case 'movendo': {
        if (!selectedUnit) return;
        const reachable = ranges.move.some((t) => samePosition(t, cursor));
        if (!reachable) {
          hooksRef.current.onInvalid?.('Fora do alcance de movimento.');
          return;
        }
        const path = pathTo(ranges.range!, cursor) ?? [];
        hooksRef.current.onConfirm?.();
        hooksRef.current.onPreviewMove?.(selectedUnit.id, path);
        setSelection({
          mode: 'menu',
          unitId: selectedUnit.id,
          destination: cursor,
          path,
        });
        return;
      }

      case 'mirando': {
        const id = selection.targets[selection.index];
        if (id === undefined) return;
        if (selection.kind === 'item') {
          perform({
            type: 'usar_item',
            unitId: selection.unitId,
            path: selection.path,
            index: selection.itemIndex ?? 0,
            ...(id !== selection.unitId ? { targetId: id } : {}),
          });
          return;
        }
        if (selection.kind === 'conversar') {
          perform({ type: 'conversar', unitId: selection.unitId, path: selection.path, targetId: id });
          return;
        }
        // Escolhido o companheiro, a tela de troca abre: a jogada ainda não
        // aconteceu, e sair dela sem mexer em nada devolve a unidade ao menu.
        if (selection.kind === 'trocar') {
          openTrade(selection, id);
          return;
        }
        perform(
          selection.kind === 'atacar'
            ? { type: 'atacar', unitId: selection.unitId, path: selection.path, targetId: id }
            : { type: 'curar', unitId: selection.unitId, path: selection.path, targetId: id },
        );
        return;
      }

      case 'itens': {
        chooseItem(selection.index);
        return;
      }

      case 'trocando': {
        moveTradeItem(selection.side, selection.index);
        return;
      }

      case 'menu': {
        const option = menuOptions[menuIndex];
        if (!option) return;
        if (option === 'voltar') {
          cancel();
        } else {
          chooseMenu(option);
        }
        return;
      }

    }
  }, [state, selection, cursor, selectedUnit, ranges, isCpuTurn, isCpuActing, perform, menuOptions, menuIndex, chooseMenu, cancel, chooseItem, moveTradeItem, openTrade, openFieldMenu, chooseField, toggleThreat]);

  const reset = useCallback(() => {
    stateRef.current = initial;
    setState(initial);
    setSelection({ mode: 'livre' });
    setIsCpuActing(false);
    setLog([]);
    setThreatIds(new Set());
    setThreatAll(false);
  }, [initial]);

  /*
   * Retomar uma partida gravada.
   *
   * O cursor vai para a primeira unidade do jogador que ainda pode jogar —
   * abrir a partida com a câmera no canto do mapa, longe de todo mundo, faria
   * quem volta no dia seguinte procurar o próprio esquadrão.
   */
  const loadState = useCallback((next: MatchState) => {
    stateRef.current = next;
    setState(next);
    setSelection({ mode: 'livre' });
    setIsCpuActing(false);
    setLog([]);
    setThreatIds(new Set());
    setThreatAll(false);
    const focus =
      next.units.find((u) => u.alive && u.team === 'jogador' && !u.hasActed) ??
      next.units.find((u) => u.alive && u.team === 'jogador') ??
      next.units[0];
    if (focus) setCursorAt(focus.position);
  }, []);

  // O cursor acompanha o alvo mirado: quem olha para a barra de previsão
  // precisa ver de quem ela fala. Quando o alvo é a própria unidade — beber a
  // poção, curar a si mesma —, ela está na casa para onde andou, e não na de
  // origem que o estado ainda registra.
  useEffect(() => {
    if (selection.mode !== 'mirando' || !target) return;
    setCursorAt(target.id === selection.unitId ? selection.destination : target.position);
  }, [selection, target]);

  const hovered = useMemo(() => unitAt(state, cursor) ?? null, [state, cursor]);

  return {
    state,
    selection,
    cursor,
    moveTiles:
      selection.mode === 'movendo' ||
      selection.mode === 'menu' ||
      selection.mode === 'trocando'
        ? ranges.move
        : [],
    attackTiles:
      selection.mode === 'movendo'
        ? ranges.attack
        : selection.mode === 'mirando'
          ? selection.targets
              .map((id) => (id === selection.unitId ? selection.destination : unitById(state, id)?.position))
              .filter((p): p is Vec2 => Boolean(p))
          : [],
    threatTiles: threat.tiles,
    threatMarks: threat.marks,
    threatIds,
    threatAll,
    path,
    forecast: plan,
    hovered,
    target,
    log,
    canAttack,
    canHeal,
    tradePartners,
    talkTargets,
    menuOptions,
    menuIndex,
    fieldOptions: FIELD_OPTIONS,
    isCpuTurn,
    isCpuActing,
    autoBattle,
    playMode,
    difficulty,
    cpuSpeed,
    setPlayMode,
    setDifficulty,
    setCpuSpeed,
    setAutoBattle,
    autoActUnit,
    moveCursor,
    setCursor,
    confirm,
    cancel,
    chooseMenu,
    chooseField,
    openFieldMenu,
    chooseItem,
    setMenuIndex,
    moveTradeItem,
    setTradeSide,
    resetTrade,
    finishTrade,
    cycleTarget,
    selectTarget,
    cycleAlly,
    toggleThreat,
    toggleThreatAtCursor,
    toggleAllThreat,
    clearThreat,
    finishPhase,
    reset,
    loadState,
  };
}

export { phaseIsOver };
